import moment from 'moment';
import numeral from 'numeral';
import React, { useState } from 'react';
import styled from 'styled-components';
import { NumberFormat } from '../constants/number-formats';
import { PaymentPlanInstalmentDesignerPreview } from '../redux/payment-plan/payment-plan.types';
import Button from './Button';
import Modal from './Modal';
import { PaymentPlanInstalmentTable } from './PaymentPlan/payment-plan';
import { Header3, P2, P3 } from './Typography';

interface PaymentPlanInstalmentDesignerModalProps {
  address: string;
  tenantName: string;
  currentUnpaidInstalmentCount: number;
  currentUnpaidTotal: number;
  currentPaymentPlanTotal: number;
  reinstatesPaymentPlan?: boolean;
  selectedFile?: File;
  preview?: PaymentPlanInstalmentDesignerPreview;
  previewError?: string;
  isPreviewing?: boolean;
  isConfirming?: boolean;
  onDownload: () => void;
  onPreview: (file: File) => void;
  onFileChange?: () => void;
  onSelectedFileChange: (file?: File) => void;
  onConfirm: (file: File) => void;
  onGoBack: () => void;
  onDismiss: () => void;
}

const SummaryCard = styled.div`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  padding: 22px 14px 12px;
  position: relative;
`;

const CardTypePill = styled.span`
  background: ${(props) => props.theme.colors.bgHoverAccent};
  border-bottom-right-radius: 4px;
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  left: 0;
  letter-spacing: 0.07em;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: 0;
`;

const SummaryRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SummaryLabel = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
`;

const SummaryValue = styled.span`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  margin-left: 16px;
  text-align: right;
`;

const SummaryOldValue = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-weight: 400;
  text-decoration: line-through;
`;

const SummaryArrow = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-size: 13px;
  font-weight: 400;
  margin: 0 8px;
`;

const SummaryGroupDivider = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin: 12px 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin: 20px 0;
`;

const SectionTitle = styled.p`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 4px;
`;

const SectionText = styled.p`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  margin: 0 0 16px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 16px;

  button {
    margin: 0;
  }
`;

const LeftAlignedAction = styled.div`
  display: flex;
  justify-content: flex-start;

  button {
    margin: 0;
  }
`;

const FileInput = styled.input`
  display: block;
  font-family: ${(props) => props.theme.typography.face.secondary};
  margin-top: 8px;
`;

const ValidationError = styled(P3)`
  color: ${(props) => props.theme.colors.negative};
  margin-bottom: 0;
  margin-top: 8px;
`;

const ErrorNotice = styled.div`
  background: #fff5f5;
  border: 1px solid ${(props) => props.theme.colors.negative};
  border-radius: 8px;
  color: ${(props) => props.theme.colors.negative};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  margin-bottom: 12px;
  padding: 10px 12px;
`;

const InfoNotice = styled.div`
  background: ${(props) => props.theme.colors.bgHoverAccent};
  border-radius: 8px;
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  margin-bottom: 12px;
  padding: 10px 12px;
`;

const DryRunHeader = styled.div`
  margin-bottom: 12px;

  h3 {
    margin-bottom: 4px;
  }

  p {
    margin-bottom: 0;
  }
`;

const SectionDivider = styled.div<{ deletion?: boolean; addition?: boolean }>`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  margin-top: 10px;

  &::before,
  &::after {
    background: ${(props) => {
      if (props.deletion) return props.theme.colors.negative;
      if (props.addition) return '#1f7a42';
      return '#F5A623';
    }};
    content: '';
    flex: 1;
    height: 1px;
    opacity: 0.4;
  }
`;

const SectionDividerText = styled.span<{ deletion?: boolean; addition?: boolean }>`
  color: ${(props) => {
    if (props.deletion) return props.theme.colors.negative;
    if (props.addition) return '#1f7a42';
    return '#B87A00';
  }};
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ScheduleDiffList = styled.div`
  max-height: 34vh;
  overflow: auto;
  padding-right: 2px;
`;

const DryRunContent = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.9fr);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DryRunPanel = styled.div`
  min-width: 0;
`;

const SchedulePreviewPanel = styled.div`
  border-left: 1px solid ${(props) => props.theme.colors.dividerLight};
  min-width: 0;
  padding-left: 16px;

  @media (max-width: 900px) {
    border-left: none;
    border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
    padding-left: 0;
    padding-top: 16px;
  }
`;

const SchedulePreviewTable = styled.div`
  max-height: 52vh;
  overflow: auto;

  table {
    min-width: 100%;
  }

  td {
    white-space: nowrap;
  }
`;

const ScheduleSummaryCard = styled.div<{ deleted?: boolean }>`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  padding: 22px 14px 12px;
  position: relative;
  opacity: ${(props) => (props.deleted ? 0.72 : 1)};
`;

const ScheduleSummaryTopRow = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const ScheduleSummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ScheduleSummaryName = styled.span<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const ScheduleSummarySubRow = styled.div<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.placeholder};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 12px;
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const ScheduleSummaryValue = styled.span<{ deleted?: boolean }>`
  align-items: center;
  color: ${(props) => props.theme.colors.body};
  display: flex;
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: 700;
  gap: 6px;
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
  white-space: nowrap;
`;

const ScheduleEmptyStateCard = styled(ScheduleSummaryCard)`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
`;

const fmt = (pence: number) => `£${numeral(pence / 100).format(NumberFormat.THOUSANDS_PENCE)}`;

const getLabels = (reinstatesPaymentPlan: boolean) =>
  reinstatesPaymentPlan
    ? { title: 'Reinstate Payment Plan', previewTitle: 'Confirm Payment Plan Reinstatement', confirm: 'Reinstate plan' }
    : { title: 'Modify Instalments', previewTitle: 'Confirm Payment Plan Changes', confirm: 'Confirm' };

const PaymentPlanInstalmentDesignerModal: React.FC<PaymentPlanInstalmentDesignerModalProps> = ({
  address,
  tenantName,
  currentUnpaidInstalmentCount,
  currentUnpaidTotal,
  currentPaymentPlanTotal,
  reinstatesPaymentPlan = false,
  selectedFile,
  preview,
  previewError,
  isPreviewing = false,
  isConfirming = false,
  onDownload,
  onPreview,
  onFileChange,
  onSelectedFileChange,
  onConfirm,
  onGoBack,
  onDismiss,
}) => {
  const [showFileError, setShowFileError] = useState(false);
  const isBusy = isPreviewing || isConfirming;
  const labels = getLabels(preview ? preview.reinstatesPaymentPlan : reinstatesPaymentPlan);

  const renderCurrentSummary = () => (
    <SummaryCard>
      <CardTypePill>Payment plan</CardTypePill>
      <SummaryRow>
        <SummaryLabel>Address</SummaryLabel>
        <SummaryValue>{address}</SummaryValue>
      </SummaryRow>
      <SummaryRow>
        <SummaryLabel>Tenant</SummaryLabel>
        <SummaryValue>{tenantName}</SummaryValue>
      </SummaryRow>
      <SummaryGroupDivider />
      <SummaryRow>
        <SummaryLabel>Unpaid instalments</SummaryLabel>
        <SummaryValue>{currentUnpaidInstalmentCount}</SummaryValue>
      </SummaryRow>
      <SummaryRow>
        <SummaryLabel>Unpaid total</SummaryLabel>
        <SummaryValue>{fmt(currentUnpaidTotal)}</SummaryValue>
      </SummaryRow>
      <SummaryRow>
        <SummaryLabel>Payment plan total</SummaryLabel>
        <SummaryValue>{fmt(currentPaymentPlanTotal)}</SummaryValue>
      </SummaryRow>
    </SummaryCard>
  );

  const renderChangedValue = (oldValue: string | number, newValue: string | number) => (
    <SummaryValue>
      {oldValue === newValue ? (
        newValue
      ) : (
        <>
          <SummaryOldValue>{oldValue}</SummaryOldValue>
          <SummaryArrow>→</SummaryArrow>
          {newValue}
        </>
      )}
    </SummaryValue>
  );

  const formatScheduleDate = (date?: string) => (date ? moment(date).format('DD/MM/YYYY') : '—');
  const formatScheduleAmount = (amountPence?: number) => (amountPence !== undefined ? fmt(amountPence) : '—');
  const renderScheduleCard = (
    currentRow: PaymentPlanInstalmentDesignerPreview['currentSchedule'][number] | undefined,
    proposedRow: PaymentPlanInstalmentDesignerPreview['proposedSchedule'][number] | undefined,
    status: 'added' | 'changed' | 'removed' | 'unchanged'
  ) => {
    const displayRow = proposedRow || currentRow;
    const deleted = status === 'removed';
    const dateChanged = Boolean(currentRow && proposedRow && currentRow.dueDate !== proposedRow.dueDate);
    const amountChanged = Boolean(currentRow && proposedRow && currentRow.amountPence !== proposedRow.amountPence);
    if (!displayRow) return null;

    return (
      <ScheduleSummaryCard deleted={deleted}>
        <CardTypePill>Instalment</CardTypePill>
        <ScheduleSummaryTopRow>
          <ScheduleSummaryLeft>
            <ScheduleSummaryName deleted={deleted}>
              {status === 'added'
                ? formatScheduleDate(proposedRow && proposedRow.dueDate)
                : status === 'removed'
                ? formatScheduleDate(currentRow && currentRow.dueDate)
                : dateChanged
                ? `${formatScheduleDate(currentRow && currentRow.dueDate)} → ${formatScheduleDate(
                    proposedRow && proposedRow.dueDate
                  )}`
                : formatScheduleDate(currentRow && currentRow.dueDate)}
            </ScheduleSummaryName>
            <ScheduleSummarySubRow deleted={deleted}>
              Instalment number: {displayRow.instalmentNumber}
            </ScheduleSummarySubRow>
          </ScheduleSummaryLeft>
          <ScheduleSummaryValue deleted={deleted}>
            {status === 'changed' && amountChanged ? (
              <>
                <SummaryOldValue>{formatScheduleAmount(currentRow && currentRow.amountPence)}</SummaryOldValue>
                <SummaryArrow>→</SummaryArrow>
                {formatScheduleAmount(proposedRow && proposedRow.amountPence)}
              </>
            ) : status === 'changed' ? (
              formatScheduleAmount(currentRow && currentRow.amountPence)
            ) : status === 'added' ? (
              formatScheduleAmount(proposedRow && proposedRow.amountPence)
            ) : (
              formatScheduleAmount(currentRow && currentRow.amountPence)
            )}
          </ScheduleSummaryValue>
        </ScheduleSummaryTopRow>
      </ScheduleSummaryCard>
    );
  };
  const getScheduleDiffStatus = (
    currentRow?: PaymentPlanInstalmentDesignerPreview['currentSchedule'][number],
    proposedRow?: PaymentPlanInstalmentDesignerPreview['proposedSchedule'][number]
  ): 'added' | 'changed' | 'removed' | 'unchanged' => {
    if (!currentRow && proposedRow) return 'added';
    if (currentRow && !proposedRow) return 'removed';
    if (!currentRow || !proposedRow) return 'unchanged';
    return currentRow.dueDate === proposedRow.dueDate && currentRow.amountPence === proposedRow.amountPence
      ? 'unchanged'
      : 'changed';
  };

  if (preview) {
    const hasTotalDifference = preview.unpaidTotalDifference !== 0;
    const totalChangeDirection = preview.unpaidTotalDifference > 0 ? 'increases' : 'decreases';
    // Pair rows by their stable instalment number (assigned by the backend) rather than by array
    // position, so reordering/insertions/deletions are labelled accurately instead of cascading.
    const currentByInstalmentNumber = new Map(preview.currentSchedule.map((row) => [row.instalmentNumber, row]));
    const proposedByInstalmentNumber = new Map(preview.proposedSchedule.map((row) => [row.instalmentNumber, row]));
    const instalmentNumbers = Array.from(
      new Set([...preview.currentSchedule, ...preview.proposedSchedule].map((row) => row.instalmentNumber))
    ).sort((a, b) => a - b);
    const scheduleDiffRows = instalmentNumbers.map((instalmentNumber) => {
      const currentRow = currentByInstalmentNumber.get(instalmentNumber);
      const proposedRow = proposedByInstalmentNumber.get(instalmentNumber);
      return {
        currentRow,
        proposedRow,
        status: getScheduleDiffStatus(currentRow, proposedRow),
      };
    });
    const modifiedRows = scheduleDiffRows.filter((row) => row.status === 'changed');
    const addedRows = scheduleDiffRows.filter((row) => row.status === 'added');
    const removedRows = scheduleDiffRows.filter((row) => row.status === 'removed');
    const proposedInstalments = preview.proposedSchedule.map((instalment, index) => ({
      id: `proposed-${index}`,
      instalmentNumber: instalment.instalmentNumber,
      dueDate: instalment.dueDate,
      amount: instalment.amountPence,
      status: 'PENDING',
    }));

    return (
      <Modal onDismiss={onDismiss} scrollable capHeight maxWidth="1080px">
        <DryRunHeader>
          <Header3>{labels.previewTitle}</Header3>
          <P2>Review the impact below before proceeding.</P2>
        </DryRunHeader>

        {preview.reinstatesPaymentPlan ? (
          <InfoNotice>
            Confirming replaces the unpaid instalments with this schedule, makes the plan active and turns off auto charge
            for this claim.
          </InfoNotice>
        ) : null}

        {hasTotalDifference ? (
          <ErrorNotice>
            Error: this upload {totalChangeDirection} the unpaid amount by{' '}
            {fmt(Math.abs(preview.unpaidTotalDifference))}. The unpaid total must match before you can confirm.
          </ErrorNotice>
        ) : null}

        <DryRunContent>
          <DryRunPanel>
            <SummaryCard>
              <CardTypePill>Payment plan</CardTypePill>
              <SummaryRow>
                <SummaryLabel>Unpaid instalments</SummaryLabel>
                {renderChangedValue(preview.currentUnpaidInstalmentCount, preview.uploadedInstalmentCount)}
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Unpaid total</SummaryLabel>
                {renderChangedValue(fmt(preview.currentUnpaidTotal), fmt(preview.uploadedTotal))}
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Payment plan total</SummaryLabel>
                {renderChangedValue(fmt(preview.currentPaymentPlanTotal), fmt(preview.proposedPaymentPlanTotal))}
              </SummaryRow>
            </SummaryCard>

            <ScheduleDiffList>
              {modifiedRows.length ? (
                <>
                  <SectionDivider>
                    <SectionDividerText>Modified</SectionDividerText>
                  </SectionDivider>
                  {modifiedRows.map(({ currentRow, proposedRow, status }, index) => (
                    <React.Fragment key={`modified-${index}`}>
                      {renderScheduleCard(currentRow, proposedRow, status)}
                    </React.Fragment>
                  ))}
                </>
              ) : null}

              {addedRows.length ? (
                <>
                  <SectionDivider addition>
                    <SectionDividerText addition>Added</SectionDividerText>
                  </SectionDivider>
                  {addedRows.map(({ currentRow, proposedRow, status }, index) => (
                    <React.Fragment key={`added-${index}`}>
                      {renderScheduleCard(currentRow, proposedRow, status)}
                    </React.Fragment>
                  ))}
                </>
              ) : null}

              {removedRows.length ? (
                <>
                  <SectionDivider deletion>
                    <SectionDividerText deletion>Deleted</SectionDividerText>
                  </SectionDivider>
                  {removedRows.map(({ currentRow, proposedRow, status }, index) => (
                    <React.Fragment key={`removed-${index}`}>
                      {renderScheduleCard(currentRow, proposedRow, status)}
                    </React.Fragment>
                  ))}
                </>
              ) : null}

              {!modifiedRows.length && !addedRows.length && !removedRows.length ? (
                <ScheduleEmptyStateCard>
                  <CardTypePill>Instalments</CardTypePill>
                  No schedule rows changed.
                </ScheduleEmptyStateCard>
              ) : null}
            </ScheduleDiffList>
          </DryRunPanel>

          <SchedulePreviewPanel>
            <SectionTitle>Proposed instalments</SectionTitle>
            <SchedulePreviewTable>
              <PaymentPlanInstalmentTable instalments={proposedInstalments} />
            </SchedulePreviewTable>
          </SchedulePreviewPanel>
        </DryRunContent>

        <ActionBar>
          <Button buttonType="secondary" onClick={onGoBack} disabled={isConfirming} noArrow>
            Go back
          </Button>
          <Button
            onClick={() => selectedFile && onConfirm(selectedFile)}
            disabled={isConfirming || !selectedFile || hasTotalDifference}
          >
            {labels.confirm}
          </Button>
        </ActionBar>
      </Modal>
    );
  }

  return (
    <Modal onDismiss={onDismiss}>
      <Header3 style={{ marginBottom: 16 }}>{labels.title}</Header3>

      {renderCurrentSummary()}

      <Divider />

      <SectionTitle>Download CSV</SectionTitle>
      <SectionText>
        Download the unpaid instalments, update the schedule, then upload the edited CSV. Every instalment must be due after
        today.
      </SectionText>
      <ActionBar>
        <Button onClick={onDownload} disabled={isBusy}>
          Download CSV
        </Button>
      </ActionBar>

      <Divider />

      <SectionTitle>Upload CSV</SectionTitle>
      <SectionText>The uploaded CSV will replace all unpaid instalments after confirmation.</SectionText>
      <FileInput
        type="file"
        accept=".csv,text/csv"
        disabled={isBusy}
        onChange={(event) => {
          const nextFile = event.target.files && event.target.files.length ? event.target.files[0] : undefined;
          setShowFileError(false);
          if (onFileChange) onFileChange();
          onSelectedFileChange(nextFile);
        }}
      />
      {showFileError ? <ValidationError>Please choose a CSV file.</ValidationError> : null}
      {previewError ? <ValidationError>{previewError}</ValidationError> : null}
      <ActionBar>
        <Button
          onClick={() => {
            if (!selectedFile) {
              setShowFileError(true);
              return;
            }
            onPreview(selectedFile);
          }}
          disabled={isBusy || !selectedFile}
        >
          {isPreviewing ? 'Previewing changes' : 'Preview changes'}
        </Button>
      </ActionBar>

      <Divider />

      <LeftAlignedAction>
        <Button buttonType="secondary" onClick={onDismiss} disabled={isBusy} noArrow>
          Close
        </Button>
      </LeftAlignedAction>
    </Modal>
  );
};

export default PaymentPlanInstalmentDesignerModal;
